/**
 * PATH: apps/hr/src/pages/clock/clockHelpers.ts
 * FILE_ID: HR-TIMECLOCK-HELPERS-001
 * PURPOSE: Pure I→O helpers for the time-clock page in apps/hr.
 * WHY: Keeps ClockPage.tsx under the 300-line cap and lets these
 *      tiny formatters / GPS adapters be unit-tested without
 *      spinning up React or jsdom. Ported from
 *      apps/workforce/src/pages/clock/clockHelpers.ts so apps/hr
 *      doesn't depend on apps/workforce.
 */

/** Format a UTC ISO timestamp as a local "Apr 5, 2026, 9:32 AM" string. */
export function formatPunchTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/** Format a minutes count as "1h 23m" / "23m" / "0m". */
export function formatDuration(totalMinutes: number): string {
  const m = Math.max(0, Math.floor(totalMinutes))
  const hours = Math.floor(m / 60)
  const mins = m % 60
  if (hours === 0) return `${mins}m`
  return `${hours}h ${mins}m`
}

/** Async GPS capture — never throws. Returns null on denial / timeout / no support. */
export function getGpsOrNull(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 30_000 },
    )
  })
}

/** Pull a structured error code out of a thrown ApiClient error string.
 *
 * The workforce-api wraps errors as
 *   {"detail": {"code": "already_clocked_in", "message": "..."}}
 * but the fetch wrapper throws a stringified `API error 409: <body>` —
 * so we regex the code out.
 */
export function extractErrorCode(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  const match = msg.match(/"code":\s*"([^"]+)"/)
  return match ? match[1] : 'unknown'
}
